import { FC } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Combobox } from "@/components/Interface/FormFields/Combobox/Combobox";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import type { Country } from "@/constants/countries";
import { useGetSortedCountries } from "@/hooks/useGetSortedCountries";
import {
  DACH_HOUSE_NUMBER_REGEX,
  DEFAULT_TEXT_FIELD_REGEX,
  getPostalCodePattern,
  INTERNATIONAL_NAME_REGEX,
} from "@/utils/constants";

import { BillingAccountFormValues } from "./billingAccountForm";

interface Props {
  disabled?: boolean;
}

/** Bezeichnung, recipient and postal address of a Vertragskonto. */
export const BillingAccountAddressFields: FC<Props> = ({
  disabled = false,
}) => {
  const { t } = useTranslation("portal", {
    keyPrefix: "user-account.billingAccounts.modal.fields",
  });
  const { t: tGlobal } = useTranslation();
  const {
    register,
    control,
    watch,
    getValues,
    trigger,
    formState: { errors },
  } = useFormContext<BillingAccountFormValues>();
  const sortedCountries = useGetSortedCountries();
  const selectedCountry = watch("country");

  const required = tGlobal("translation:label.validation.required");
  const genericRegexError = tGlobal(
    "translation:Common.generalForm.error.genericRegexError",
  );
  const textPattern = {
    value: DEFAULT_TEXT_FIELD_REGEX,
    message: genericRegexError,
  };
  const namePattern = { ...textPattern, value: INTERNATIONAL_NAME_REGEX };
  // Same as PersonalDetails: trim so padding from API/paste does not trip regexes.
  const trimOnChange = { setValueAs: (value: string) => value?.trim() ?? "" };
  const fieldProps = { register, errors, variant: "dark" as const };

  // Invoice recipient needs a company name or a person name (OR), never neither.
  const validateCompanyOrName = () => {
    const { companyName, firstName, lastName } = getValues();
    const hasCompany = !!companyName?.trim();
    const hasName = !!firstName?.trim() || !!lastName?.trim();

    return hasCompany || hasName || t("companyOrNameRequired");
  };

  const revalidateCompanyOrName = () => {
    void trigger(["companyName", "firstName", "lastName"]);
  };

  const companyOrNameOptions = {
    required: false,
    validate: validateCompanyOrName,
    onChange: revalidateCompanyOrName,
    disabled,
    ...trimOnChange,
  };

  return (
    <div className="grid grid-cols-1 gap-2.5 tablet:grid-cols-2 tablet:gap-x-3">
      <div className="flex flex-col gap-2 tablet:col-span-2">
        <FormField<BillingAccountFormValues>
          id="billing-account-alias"
          name="alias"
          label={t("alias")}
          options={{
            required,
            maxLength: 80,
            pattern: textPattern,
            disabled,
            ...trimOnChange,
          }}
          {...fieldProps}
        />
        <p className="text-body-xs leading-md text-foreground-primary-inverse">
          {t("aliasHint")}
        </p>
      </div>
      <FormField<BillingAccountFormValues>
        id="billing-account-first-name"
        name="firstName"
        label={t("firstName")}
        options={{
          ...companyOrNameOptions,
          maxLength: 40,
          pattern: namePattern,
        }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-last-name"
        name="lastName"
        label={t("lastName")}
        options={{
          ...companyOrNameOptions,
          maxLength: 40,
          pattern: namePattern,
        }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-company-name"
        className="tablet:col-span-2"
        name="companyName"
        label={t("companyName")}
        options={{
          ...companyOrNameOptions,
          maxLength: 80,
          pattern: textPattern,
        }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-additional-address"
        className="tablet:col-span-2"
        name="additionalAddress"
        label={t("additionalAddress")}
        options={{
          required: false,
          maxLength: 40,
          pattern: textPattern,
          disabled,
          ...trimOnChange,
        }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-street"
        name="street"
        label={tGlobal(
          "Onboarding.sections.personal-details.form.label.street",
        )}
        options={{
          required,
          maxLength: 40,
          pattern: textPattern,
          disabled,
          ...trimOnChange,
        }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-house-number"
        name="houseNumber"
        label={tGlobal(
          "Onboarding.sections.personal-details.form.label.houseNumber",
        )}
        options={{
          required,
          maxLength: 10,
          pattern: {
            value: DACH_HOUSE_NUMBER_REGEX,
            message: genericRegexError,
          },
          disabled,
          ...trimOnChange,
        }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-zip"
        name="zip"
        label={t("zip")}
        options={{
          required,
          maxLength: 10,
          pattern: {
            value: getPostalCodePattern(selectedCountry),
            message: genericRegexError,
          },
          disabled,
          ...trimOnChange,
        }}
        {...fieldProps}
      />
      <FormField<BillingAccountFormValues>
        id="billing-account-city"
        name="city"
        label={t("city")}
        options={{
          required,
          maxLength: 40,
          pattern: textPattern,
          disabled,
          ...trimOnChange,
        }}
        {...fieldProps}
      />
      <div className="tablet:col-span-2">
        <Controller
          control={control}
          name="country"
          render={({ field }) => (
            <Combobox<Country>
              dataTestId="billing-account-country"
              field={field}
              variant="dark"
              label={tGlobal(
                "Onboarding.sections.personal-details.form.label.country",
              )}
              displayValue={(country?: Country) =>
                country
                  ? tGlobal(`translation:countries.${country.ALPHA3}`)
                  : ""
              }
              options={sortedCountries}
              placeholder={tGlobal(
                "Onboarding.sections.personal-details.form.label.country",
              )}
              disabled={disabled}
            />
          )}
        />
      </div>
    </div>
  );
};
